import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { SessionListResponse } from '../../../../interfaces/sessions';
import { SESSION_METRICS_COLUMNS, CustomMetricColumns, CustomDetailColumns } from '../../../../interfaces/table';
import { CustomDefinition } from '../../../../interfaces/db';
import { getCustomCode, CustomCode } from '../../../../components/CustomScores/cfg';
import { formatCustomScore, FormattedCustomScore } from './custom-score-formatter-hook';
import { SessionDetailView } from '../../../../components/History/views';
import { convertFromReduxSafeCustomDefinitions } from '../../users/slice';

export interface UseSessionListCustomScoresProps {
  sessions: SessionListResponse[];
  view?: SessionDetailView;
}

export interface SessionCustomScoresSummary {
  sessionId: string;
  hasCustomScores: boolean;
  customScoresCount: number;
  scoresSummary: FormattedCustomScore[];
}

/**
 * Hook specifically for SessionList component to get custom scores for sessions.
 * Only processes sessions that have a 'connect' (live_duration > 0).
 * Only processes custom scores if view is MAIN or ANALYTICS.
 * Reads custom scores directly from the session list data (no additional API calls).
 * Returns a simplified view for display in the session list.
 */
export function useSessionListCustomScores({
  sessions,
  view
}: UseSessionListCustomScoresProps) {
  
  // Get custom definitions from Redux state
  const reduxSafeDefinitions = useSelector((state: RootState) => state.customDefinitions.value);
  const customDefinitions = reduxSafeDefinitions ? convertFromReduxSafeCustomDefinitions(reduxSafeDefinitions) : null;
  const teamId = useSelector((state: RootState) => state.team.value?.team_id);
  
  // Get team-specific custom codes (like Attyx codes)
  const teamCustomCodes = useMemo(() => getCustomCode(teamId || ''), [teamId]);
  
  const hasCustomDefinitions = customDefinitions && customDefinitions.length > 0;
  const hasTeamCustomCodes = teamCustomCodes && teamCustomCodes.length > 0;
  
  // Process sessions and extract custom scores
  const sessionCustomScoresSummary = useMemo((): Record<string, SessionCustomScoresSummary> => {
    const summary: Record<string, SessionCustomScoresSummary> = {};
    
    // Only process custom scores for MAIN and ANALYTICS views
    if (!hasCustomDefinitions || (view && view !== SessionDetailView.MAIN && view !== SessionDetailView.ANALYSIS)) {
      return summary;
    }
    
    sessions.forEach(session => {
      // Only process sessions with live duration > 0 (connect/live person)
      const liveDuration = session.result?.get(SESSION_METRICS_COLUMNS.LIVE_DURATION);
      if (!liveDuration || Number(liveDuration) <= 0) {
        return;
      }
      
      // Extract custom scores from session data
      const rawScores: Array<{
        definition: CustomDefinition;
        rawValue: any;
        explanation?: string;
      }> = [];
      
      // Get explanation data
      let explanationMap: Record<string, string> = {};
      try {
        const explanationData = session.result?.get(CustomDetailColumns.METRIC_TO_EXPLANATION_JSON_STR);
        if (explanationData && typeof explanationData === 'string') {
          explanationMap = JSON.parse(explanationData);
        }
      } catch (e) {
        // Ignore parse errors
      }
      
      // Process custom definitions (following ScoreMetric.tsx pattern)
      customDefinitions.forEach((definition: CustomDefinition) => {
        if (definition.metric_idx !== null && definition.metric_idx >= 0 && definition.metric_idx <= 19) {
          const metricColumnKey = `METRIC_${definition.metric_idx}` as keyof typeof CustomMetricColumns;
          const metricColumn = CustomMetricColumns[metricColumnKey];
          
          if (metricColumn) {
            const rawValue = session.result?.get(metricColumn);
            const explanation = explanationMap[definition.metric_idx.toString()] || undefined;
            
            rawScores.push({
              definition,
              rawValue,
              explanation
            });
          }
        }
      });
      
      
      // Format scores using the formatter utility
      const formattedScores = rawScores
        .map(score => formatCustomScore({
          session,
          definition: score.definition,
          rawValue: score.rawValue,
          explanation: score.explanation,
          teamCustomCodes
        }))
        .filter((score): score is FormattedCustomScore => score !== null);
      
      // Create summary of up to 2 most important scores for display in list
      const scoresSummary = formattedScores.slice(0, 2);
      
      summary[session.session_id] = {
        sessionId: session.session_id,
        hasCustomScores: formattedScores.length > 0,
        customScoresCount: formattedScores.length,
        scoresSummary
      };
    });
    
    return summary;
  }, [sessions, customDefinitions, hasCustomDefinitions, teamId, view]);

  return {
    // Main data
    sessionCustomScoresSummary,
    
    // Status
    hasCustomDefinitions,
    hasTeamCustomCodes,
    isLoadingCustomScores: false, // No loading since we read from existing data
    
    // Utility
    getCustomScoresForSession: (sessionId: string) => sessionCustomScoresSummary[sessionId] || null,
    hasCustomScoresForSession: (sessionId: string) => {
      const summary = sessionCustomScoresSummary[sessionId];
      return summary?.hasCustomScores || false;
    }
  };
}